import { type UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { DependenciesApi, type DependencyResponse } from '@/lib/api/item-store/dependencies';
import { invalidateAfterDependencyChange } from '@/lib/utils/itemCacheUtils';
import { InvalidDependencyPairError, SelfDependencyError } from './dependencyErrors';

interface CreateDependencyVariables {
  predecessorId: number;
  successorId: number;
}

interface CreateDependencyResult {
  response: DependencyResponse;
  otherId: number;
}

interface UseCreateDependencyMutationParams {
  currentItemId: number;
  options?: Omit<
    UseMutationOptions<CreateDependencyResult, Error, CreateDependencyVariables>,
    'mutationFn'
  >;
}

export const useCreateDependencyMutation = ({
  currentItemId,
  options,
}: UseCreateDependencyMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation<CreateDependencyResult, Error, CreateDependencyVariables>({
    ...options,
    mutationFn: async ({ predecessorId, successorId }) => {
      const isPredecessorCurrent = predecessorId === currentItemId;
      const isSuccessorCurrent = successorId === currentItemId;
      if (isPredecessorCurrent && isSuccessorCurrent) {
        throw new SelfDependencyError();
      }
      if (!isPredecessorCurrent && !isSuccessorCurrent) {
        throw new InvalidDependencyPairError();
      }
      const otherId = isPredecessorCurrent ? successorId : predecessorId;
      const body = isPredecessorCurrent
        ? { itemId: currentItemId, successorId: otherId }
        : { itemId: currentItemId, predecessorId: otherId };
      const response = await DependenciesApi.createDependency(body);
      return { response, otherId };
    },
    onSuccess: (data, variables, ...rest) => {
      const { otherId } = data;
      // Defer invalidation to the next microtask so the caller's UI updates
      // (e.g. closing the add picker) commit before suspense queries refetch.
      queueMicrotask(() => invalidateAfterDependencyChange(queryClient, currentItemId, otherId));
      options?.onSuccess?.(data, variables, ...rest);
    },
  });
};
